/* eslint-disable max-lines */
import { CONTRACT_RATE, ReferentPriceTypes } from '@strata/constants';
import { capitalizeString, formatNameLastFirst, sortAsc } from '@strata/helper-functions';
import { DialogWithForm } from '@strata/react-dialog';
import { BidInput, TextAreaFormik } from '@strata/react-inputs';
import { RadioButtonGroupFormik } from '@strata/react-radio-button-group';
import { SuggestMultiSelectFormik, SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast, WarningToast } from '@strata/react-toast-message';
import { validationStrings } from '@strata/strata-ui-strings';
import Decimal from 'decimal.js';
import { Formik, useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { Fragment, useMemo } from 'react';
import * as Yup from 'yup';

import { handleError } from 'services/ErrorManagement';
import { post, useGet } from 'services/Http';
import {
    filterListItems,
    getListItemName,
    ListSchema,
    ListType,
    ListTypeItemKeyMap,
    pluralizeListTypeName,
    validateNpis,
} from 'services/ListManagementHelpers';

const ProductType = {
    BRAND: 'brand',
    GENERIC: 'generic',
    BOTH: 'both',
};
const Referent = {
    AWP: ReferentPriceTypes.AWP,
    ASP: ReferentPriceTypes.ASP,
    CONTRACT: 'Contract',
};

/* eslint-disable react/prop-types */
const DialogContentForType = {
    [ListType.account]: {
        validationSchema: Yup.object({
            accounts: Yup.string().required(validationStrings.requiredField.errorMessage),
        }),
        packageListItems: (values, listType) => {
            // Check for invalid characters
            if (/[^\w\s,]/.test(values.accounts)) {
                handleError('Accounts formatted incorrectly.');
                return null;
            }

            // Split & trim
            const accountsToAdd = (values.accounts?.split(/[\r\n,]/) ?? [])
                .map((item) => item.trim())
                .filter((item) => item);

            // Map deduped ids to schema
            return [...new Set(accountsToAdd)].map((item) => ({
                [ListTypeItemKeyMap[listType][0]]: item,
            }));
        },
        DialogContent: () => (
            <>
                <p className="margin__bottom--medium">Add accounts by entering below.</p>
                <TextAreaFormik
                    id="accounts"
                    label="Accounts"
                    required
                    helperText="Separate multiple accounts using a comma (e.g., 000000,111111) or insert one account per line."
                />
            </>
        ),
    },
    [ListType.group]: {
        validationSchema: Yup.object({
            groupIds: Yup.string().required(validationStrings.requiredField.errorMessage),
        }),
        packageListItems: (values, listType) => {
            // Check for invalid characters
            if (/[^\w\s,]/.test(values.groupIds)) {
                handleError('Group IDs formatted incorrectly.');
                return null;
            }

            // Split & trim
            const groupIdsToAdd = (values.groupIds?.split(/[\r\n,]/) ?? [])
                .map((item) => item.trim())
                .filter((item) => item);

            // Map deduped ids to schema
            return [...new Set(groupIdsToAdd)].map((item) => ({
                [ListTypeItemKeyMap[listType][0]]: item,
            }));
        },
        DialogContent: () => (
            <>
                <p className="margin__bottom--medium">Add group IDs by entering below.</p>
                <TextAreaFormik
                    id="groupIds"
                    label="Group IDs"
                    required
                    helperText="Separate multiple group IDs using a comma (e.g., 000000,111111) or insert one group ID per line."
                />
            </>
        ),
    },
    [ListType.network]: {
        validationSchema: Yup.object({
            npis: Yup.string().required(validationStrings.requiredField.errorMessage),
        }),
        packageListItems: async (values, listType) => {
            const npisToAdd = validateNpis(values);
            if (!npisToAdd) {
                return null;
            }

            // Lookup suppliers
            const response = await post('/reference/suppliers/bulk_lookup', npisToAdd);
            const suppliersToAdd = response?.suppliers ?? [];

            // Check to see if any NPIs were not found
            const invalidNpis = (response?.not_found ?? []).sort(sortAsc);

            if (invalidNpis.length > 0) {
                handleError(
                    `The following NPIs were not found. Please correct them and try again: ${invalidNpis.join(
                        ', ',
                    )}`,
                );
                return null;
            }

            return suppliersToAdd.map((supplier) => ({
                [ListTypeItemKeyMap[listType][0]]: supplier.organization_name,
                [ListTypeItemKeyMap[listType][1]]: supplier.supplier_npi,
            }));
        },
        DialogContent: () => (
            <>
                <p className="margin__bottom--medium">
                    Add pharmacies by entering their NPI number below.
                </p>
                <TextAreaFormik
                    id="npis"
                    label="NPI"
                    required
                    helperText="Separate multiple NPI numbers using a comma (e.g., 0000000000,1111111111) or insert one NPI number per line."
                />
            </>
        ),
    },
    [ListType.supplier]: {
        validationSchema: Yup.object({
            npis: Yup.string().required(validationStrings.requiredField.errorMessage),
        }),
        packageListItems: async (values, listType) => {
            const npisToAdd = validateNpis(values);
            if (!npisToAdd) {
                return null;
            }

            // Lookup suppliers
            const response = await post('/reference/suppliers/bulk_lookup', npisToAdd);
            const suppliersToAdd = response?.suppliers ?? [];

            // Check to see if any NPIs were not found
            const invalidNpis = (response?.not_found ?? []).sort(sortAsc);

            if (invalidNpis.length > 0) {
                handleError(
                    `The following NPIs were not found. Please correct them and try again: ${invalidNpis.join(
                        ', ',
                    )}`,
                );
                return null;
            }

            return suppliersToAdd.map((supplier) => ({
                [ListTypeItemKeyMap[listType][0]]: supplier.organization_name,
                [ListTypeItemKeyMap[listType][1]]: supplier.supplier_npi,
            }));
        },
        DialogContent: () => (
            <>
                <p className="margin__bottom--medium">
                    Add pharmacies by entering their NPI number below.
                </p>
                <TextAreaFormik
                    id="npis"
                    label="NPI"
                    required
                    helperText="Separate multiple NPI numbers using a comma (e.g., 0000000000,1111111111) or insert one NPI number per line."
                />
            </>
        ),
    },
    [ListType.prescriber]: {
        validationSchema: Yup.object({
            npis: Yup.string().required(validationStrings.requiredField.errorMessage),
        }),
        packageListItems: async (values, listType) => {
            const npisToAdd = validateNpis(values);
            if (!npisToAdd) {
                return null;
            }

            // Lookup prescribers
            const response = await post('/reference/prescribers/bulk_lookup', npisToAdd);
            const prescribersToAdd = response?.prescribers ?? [];

            // Check to see if any NPIs are not valid Type 1 NPIs
            const invalidNpis = [
                ...(response?.not_found ?? []),
                ...prescribersToAdd
                    .filter((prescriber) => !prescriber.first_name)
                    .map((prescriber) => prescriber.prescriber_npi),
            ].sort(sortAsc);

            if (invalidNpis.length > 0) {
                WarningToast({
                    message: `The following NPIs are not Type 1 NPIs. They have not been added to the list: ${invalidNpis.join(
                        ', ',
                    )}`,
                });
            }

            return prescribersToAdd.map((prescriber) => ({
                [ListTypeItemKeyMap[listType][0]]: formatNameLastFirst(
                    prescriber.last_name,
                    prescriber.first_name,
                ),
                [ListTypeItemKeyMap[listType][1]]: prescriber.prescriber_npi,
            }));
        },
        DialogContent: () => (
            <>
                <p className="margin__bottom--medium">
                    Add prescribers by entering their NPI number below.
                </p>
                <TextAreaFormik
                    id="npis"
                    label="NPI"
                    required
                    helperText="Separate multiple NPI numbers using a comma (e.g., 0000000000,1111111111) or insert one NPI number per line."
                />
            </>
        ),
    },
    [ListType.product]: {
        validationSchema: Yup.object({
            productServiceOid: Yup.string().required(validationStrings.requiredField.errorMessage),
            brandProductDescription: Yup.string().required(
                validationStrings.requiredField.errorMessage,
            ),
            genericProductDescription: Yup.string().required(
                validationStrings.requiredField.errorMessage,
            ),
            productType: Yup.string().required(validationStrings.requiredField.errorMessage),
            brandReferent: Yup.string(),
            brandRateOverride: BidInput.validator().when('brandReferent', {
                is: (brandReferent) =>
                    Object.values(Referent)
                        .filter((val) => val !== Referent.CONTRACT)
                        .includes(brandReferent),
                then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
            }),
            genericReferent: Yup.string(),
            genericRateOverride: BidInput.validator().when('genericReferent', {
                is: (genericReferent) =>
                    Object.values(Referent)
                        .filter((val) => val !== Referent.CONTRACT)
                        .includes(genericReferent),
                then: (schema) => schema.required(validationStrings.requiredField.errorMessage),
            }),
        }),
        packageListItems: (values, listType) => {
            const productTypesToAdd =
                values.productType === ProductType.BOTH
                    ? [ProductType.BRAND, ProductType.GENERIC]
                    : [values.productType];

            const formatRateOverride = (rate) =>
                rate != null && !Number.isNaN(rate) ? Decimal.div(rate, 100).toNumber() : rate;

            return productTypesToAdd.map((type) => ({
                [ListTypeItemKeyMap[listType][0]]: values[`${type}ProductDescription`],
                [ListTypeItemKeyMap[listType][1]]: values.productServiceOid,
                [ListTypeItemKeyMap[listType][2]]:
                    values[`${type}Referent`] !== Referent.CONTRACT
                        ? formatRateOverride(values[`${type}RateOverride`])
                        : CONTRACT_RATE,
                referent_price_type:
                    values[`${type}Referent`] !== Referent.CONTRACT
                        ? values[`${type}Referent`]
                        : undefined,
                is_brand: type === ProductType.BRAND,
            }));
        },
        DialogContent: ({ listSchema }) => {
            const formik = useFormikContext();

            const products = useGet('/reference/product_benefit_configs/');
            const productOptions = useMemo(
                () =>
                    products
                        ?.filter((product) => product.configured)
                        ?.map((product) => ({
                            name: product.description,
                            value: product.oid,
                            secondaryData: product.oid,
                            brandProductDescription: product.brand_product_description,
                            genericProductDescription: product.generic_product_description,
                        }))
                        .sort((a, b) => sortAsc(a.name, b.name)),
                [products],
            );

            return (
                <>
                    <SuggestSingleSelectFormik
                        id="productServiceOid"
                        label="Product"
                        required
                        values={productOptions}
                        isLoading={!productOptions}
                        selectedItem={productOptions?.find(
                            ({ value }) => value === formik.values.productServiceOid,
                        )}
                        onChange={(newVal) =>
                            formik.setValues({
                                productServiceOid: newVal?.value,
                                brandProductDescription: newVal?.brandProductDescription,
                                genericProductDescription: newVal?.genericProductDescription,
                            })
                        }
                    />
                    <RadioButtonGroupFormik
                        name="productType"
                        label="Product Type"
                        required
                        options={Object.values(ProductType).map((value) => ({
                            text: capitalizeString(value),
                            value,
                        }))}
                    />
                    {listSchema === ListSchema.assignment &&
                        formik.values.productType &&
                        [ProductType.BRAND, ProductType.GENERIC].map((val) =>
                            [val, ProductType.BOTH].includes(formik.values.productType) ? (
                                <Fragment key={val}>
                                    <div className="margin__bottom--medium font-bold--uppercase">
                                        {val} Override
                                    </div>
                                    <SuggestSingleSelectFormik
                                        id={`${val}Referent`}
                                        label="Default Rate Override"
                                        values={Object.values(Referent)}
                                    />
                                    {formik.values[`${val}Referent`] &&
                                        formik.values[`${val}Referent`] !== Referent.CONTRACT && (
                                            <BidInput
                                                id={`${val}RateOverride`}
                                                label={`${val} Rate Override`}
                                                required
                                                referentPriceType={formik.values[`${val}Referent`]}
                                            />
                                        )}
                                </Fragment>
                            ) : null,
                        )}
                </>
            );
        },
    },
    [ListType.therapeutic_category]: {
        validationSchema: Yup.object({
            therapeuticCategories: Yup.array()
                .required(validationStrings.requiredField.errorMessage)
                .min(1, validationStrings.requiredField.errorMessage),
        }),
        packageListItems: (values, listType) =>
            values.therapeuticCategories.map((item) => ({
                [ListTypeItemKeyMap[listType][0]]: item,
            })),
        DialogContent: ({ listType, listFormik }) => {
            const therapeuticCategoryList = useGet('/reference/therapeutic_categories/');

            const therapeuticCategoryOptions = useMemo(
                () =>
                    therapeuticCategoryList
                        ?.filter(
                            (category) =>
                                !listFormik.values.listItems?.find(
                                    (item) => category === item[ListTypeItemKeyMap[listType][0]],
                                ),
                        )
                        .sort(sortAsc),
                [listFormik.values.listItems, listType, therapeuticCategoryList],
            );

            return (
                <SuggestMultiSelectFormik
                    id="therapeuticCategories"
                    label="Therapeutic Categories"
                    required
                    isLoading={!therapeuticCategoryOptions}
                    values={therapeuticCategoryOptions}
                />
            );
        },
    },
    [ListType.zip_code]: {
        validationSchema: Yup.object({
            zipCodes: Yup.string().required(validationStrings.requiredField.errorMessage),
        }),
        packageListItems: (values, listType) => {
            // Check for invalid characters
            if (/[^\d\s,]/.test(values.zipCodes)) {
                handleError('ZIP codes formatted incorrectly.');
                return null;
            }

            // Split & trim zips
            const zipsToAdd = (values.zipCodes?.split(/[\r\n,]/) ?? [])
                .map((item) => item.trim())
                .filter((item) => item);

            // Check for zips with length !== 5
            const invalidZip = zipsToAdd.find((zip) => zip.length !== 5);
            if (invalidZip) {
                handleError(`Incorrect length for ZIP code "${invalidZip}".`);
                return null;
            }

            // Map deduped zip codes to schema
            return [...new Set(zipsToAdd)].map((item) => ({
                [ListTypeItemKeyMap[listType][0]]: item,
            }));
        },
        DialogContent: () => (
            <>
                <p className="margin__bottom--medium">Add ZIP codes by entering below.</p>
                <TextAreaFormik
                    id="zipCodes"
                    label="ZIP Codes"
                    required
                    helperText="Separate multiple ZIP codes using a comma (e.g., 15222,16037) or insert one ZIP code per line."
                />
            </>
        ),
    },
};
/* eslint-enable react/prop-types */

export const AddListItemsDialog = ({ isOpen, listSchema, listType, onClose }) => {
    const listFormik = useFormikContext();
    const listItemName =
        listType !== ListType.product
            ? pluralizeListTypeName(getListItemName(listType))
            : getListItemName(listType);

    const { validationSchema, packageListItems, DialogContent } = DialogContentForType[listType];

    return (
        <Formik
            initialValues={{}}
            enableReinitialize
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) => {
                try {
                    // Update list item values
                    const itemsToAdd = await packageListItems(values, listType);

                    if (itemsToAdd) {
                        await listFormik.setValues({
                            ...listFormik.values,
                            listItems: [
                                ...filterListItems(
                                    listFormik.values.listItems ?? [],
                                    itemsToAdd,
                                    listType,
                                ),
                                ...itemsToAdd,
                            ],
                            addedItems: [
                                ...listFormik.values.addedItems,
                                ...filterListItems(
                                    itemsToAdd,
                                    listFormik.values.addedItems,
                                    listType,
                                ),
                            ],
                            deletedItems: filterListItems(
                                listFormik.values.deletedItems,
                                itemsToAdd,
                                listType,
                            ),
                            selected: [],
                            itemsToDelete: [],
                        });
                        SuccessToast({
                            message: `Added ${itemsToAdd.length} ${listItemName} to the list.`,
                        });
                        resetForm();
                        onClose();
                    }
                } catch (err) {
                    handleError('An error occurred adding list items. Please try again.', err);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={`Add ${listItemName}`}
                    primaryButtonLabel={`Add ${listItemName}`}
                    secondaryButtonLabel="Cancel"
                    width={600}
                    isOpen={isOpen}
                    onSecondaryButtonClick={() => {
                        formik.handleReset();
                        onClose();
                    }}
                    isPrimaryButtonDisabled={
                        !(formik.dirty && formik.isValid) || formik.isSubmitting
                    }
                    dialogContent={
                        <DialogContent
                            listSchema={listSchema}
                            listType={listType}
                            listFormik={listFormik}
                        />
                    }
                />
            )}
        </Formik>
    );
};

AddListItemsDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    listSchema: PropTypes.oneOf(Object.values(ListSchema)).isRequired,
    listType: PropTypes.string.isRequired,
    onClose: PropTypes.func.isRequired,
};
